/**
 * Смена статуса байка: что скаут может выставить, откуда куда можно и на
 * какую ручку это уходит.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠ ПОЧЕМУ ЭТО ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ ЧАСТЬ api/vehicles.ts
 * ─────────────────────────────────────────────────────────────────────────
 * Здесь живёт ЕДИНСТВЕННЫЙ источник правды про URL смены статуса, и он нужен
 * двоим сразу: экранам (`api/vehicles.ts` — подписи и блокировки кнопок) и
 * офлайн-очереди (`lib/offline/outbox.ts` — сам маршрут отправки). Положить
 * его в `api/vehicles.ts` нельзя: тот импортирует `enqueue` из очереди, и
 * обратный импорт замкнул бы цикл. Отсюда третий, чистый модуль без запросов.
 *
 * Это не вкусовщина, а починка настоящего бага. Карта `ACTION_PATH` жила в
 * `api/vehicles.ts` вместе с тестом на неё, а очередь строила путь
 * самостоятельно — `action.replaceAll('_', '-')`. Для пяти действий из шести
 * результат случайно совпадал, а для `available` давал `/available` вместо
 * `/recovered`, то есть 404. Тест при этом был зелёным: он проверял функцию,
 * которую в реальной отправке никто не вызывал.
 *
 * Мораль, ради которой этот комментарий: проверять надо ту функцию, которой
 * пользуется продакшен-путь. Вторая реализация того же преобразования рядом с
 * первой — это не дублирование, это будущее расхождение.
 */

/**
 * Что скаут может выставить. Список закрыт намеренно.
 *
 * ПОРЯДОК ЗДЕСЬ — ПОРЯДОК КНОПОК НА ЭКРАНЕ, и он не алфавитный: сначала то,
 * что возвращает байк в работу, потом перемещения, и только последним —
 * «Утерян», рядом с которым не должно быть ничего, что нажимают часто.
 *
 * `at_base` — конец маршрута вывоза: байк доехал до хаба и стоит там.
 * `servicing` — сдан на стенд; `MarkServicing` заведена ровно под эту кнопку.
 *
 * ⚠ `available` — это `MarkRecovered`, и открыт скауту он был НЕ всегда.
 * Раньше возврат в оборот оставался у диспетчера именно потому, что это откат
 * скаутской пометки. Решение изменено владельцем продукта; компенсация теперь
 * целиком следовая — журнал с `source=scout`. Подробности и причина — в
 * комментарии над `MarkRecovered` в `public-api/internal/service/public.go`.
 */
export const SCOUT_STATUS_ACTIONS = [
  'available',
  'at_base',
  'in_transit',
  'servicing',
  'faulty',
  'lost',
] as const
export type ScoutStatusAction = (typeof SCOUT_STATUS_ACTIONS)[number]

export const statusActionLabel: Record<ScoutStatusAction, string> = {
  // Не «Доступен»: кнопка называется действием, а не результатом, иначе
  // читается как признак состояния, а не как то, что сейчас произойдёт.
  available: 'Вернуть в линию',
  at_base: 'На базе',
  in_transit: 'В машине',
  servicing: 'В ремонт',
  faulty: 'Сломан',
  lost: 'Утерян',
}

/** Человеческие имена статусов транспорта — для текстов про «из статуса X». */
export const vehicleStatusLabel: Record<string, string> = {
  available: 'В линии',
  reserved: 'Забронирован',
  in_ride: 'В поездке',
  faulty: 'Сломан',
  lost: 'Утерян',
  in_transit: 'В машине',
  at_base: 'На базе',
  servicing: 'В сервисе',
  written_off: 'Списан',
  in_pool: 'В пуле',
}

/**
 * Подпись по держателю (волна 3): `in_pool`/`in_transit` держит не только
 * водитель пула, но и сборщик — новых статусов для него нет, есть `poolKind`.
 * `null` — не сборщик: подпись решает сам статус.
 */
export function heldByCollectorLabel(status: string, poolKind: string): string | null {
  if (poolKind !== 'collector') return null
  if (status === 'in_pool') return 'Забронирован сборщиком'
  if (status === 'in_transit') return 'В машине у сборщика'
  return null
}

/**
 * ЗЕРКАЛО МАШИНЫ СОСТОЯНИЙ `fleet/internal/biz/vehicle.go`, allowedTransitions.
 *
 * ⚠ Расхождение здесь — это кнопка, обещающая то, чего сервер не сделает.
 * При правке той таблицы правится и эта.
 *
 * Зачем зеркало вообще: действие уходит через офлайн-очередь. Без локальной
 * проверки скаут узнает о невозможном переходе только когда появится сеть — то
 * есть, скорее всего, уже уехав от байка.
 *
 * Перечислены только переходы в те статусы, которые доступны скауту;
 * остальные строки таблицы сюда не переносятся, чтобы не создавать
 * впечатление полной копии.
 */
const ALLOWED_TO: Record<ScoutStatusAction, readonly string[]> = {
  // in_ride отсутствует НЕ по недосмотру: байк под райдером в машину не грузят.
  in_transit: ['available', 'faulty', 'lost', 'at_base', 'servicing'],
  // Тот же набор источников, что у in_transit, и по той же причине: на базу
  // байк попадает и из машины, и своим ходом с улицы. in_ride снова нет —
  // байк под райдером на базу не ставят.
  at_base: ['available', 'faulty', 'lost', 'in_transit', 'servicing'],
  faulty: ['available', 'reserved', 'in_ride', 'in_transit', 'at_base', 'servicing'],
  lost: ['available', 'reserved', 'in_ride', 'faulty', 'in_transit', 'at_base', 'servicing'],
  // reserved и in_ride отсутствуют: байк под клиентом на стенд не забирают,
  // сначала кончается аренда.
  servicing: ['available', 'faulty', 'lost', 'in_transit', 'at_base'],
  // Самый широкий набор источников, и это не щедрость, а таблица fleet:
  // в оборот байк возвращается откуда угодно, кроме списания.
  available: [
    'reserved',
    'in_ride',
    'faulty',
    'lost',
    'in_transit',
    'at_base',
    'servicing',
  ],
}

/**
 * Можно ли перевести байк из `from` в `to`.
 *
 * Переход в самого себя сервер отклоняет (`canTransition` возвращает false при
 * from == to), поэтому и здесь он запрещён: «пометить сломанным сломанный» —
 * самый частый полевой случай, когда двое скаутов приехали на один байк.
 * `written_off` — терминальный статус, из него выходов нет вовсе.
 */
export function canScoutSet(from: string, to: ScoutStatusAction): boolean {
  if (from === to) return false
  return ALLOWED_TO[to].includes(from)
}

/** Почему кнопка неактивна. Null — активна. */
export function statusBlockedReason(from: string, to: ScoutStatusAction): string | null {
  if (from === to) return `Байк уже в статусе «${vehicleStatusLabel[to] ?? to}»`
  if (from === 'written_off') return 'Байк списан — статус больше не меняется'
  // Сервер переходы из in_pool допускает (диспетчеру), но в поле они
  // оставили бы пункт пула висеть: у водителя есть «Забрал»/«Поставил».
  if (from === 'in_pool') return 'Байк в пуле водителя — статус меняют кнопки пула'
  if (!canScoutSet(from, to)) {
    const now = vehicleStatusLabel[from] ?? from
    return `Из статуса «${now}» так нельзя`
  }
  return null
}

/**
 * Сегмент URL для действия.
 *
 * ⚠ ЭТО НЕ ПРЕОБРАЗОВАНИЕ СТРОКИ, А ТАБЛИЦА, и заменять её на
 * `replaceAll('_', '-')` нельзя, как бы похоже ни выглядели пять строк из
 * шести. Действие называется по результату (`available`), а ручка — по смыслу
 * (`MarkRecovered` → `/recovered`). Ровно на этом расхождении «Вернуть в
 * линию» уходило в 404.
 */
const ACTION_PATH: Record<ScoutStatusAction, string> = {
  // Дефис в URL против подчёркивания в статусе — не опечатка, а два разных
  // имени одной вещи.
  in_transit: 'in-transit',
  at_base: 'at-base',
  faulty: 'faulty',
  lost: 'lost',
  servicing: 'servicing',
  available: 'recovered',
}

/**
 * Путь ручки для действия.
 *
 * Бросает на незнакомом действии, а не «собирает что-нибудь»: молча
 * построенный несуществующий URL и был исходным багом. Пусть лучше упадёт
 * при постановке в очередь, чем через полчаса вернётся 404 от сервера.
 */
export function statusActionPath(action: string): string {
  const path = ACTION_PATH[action as ScoutStatusAction]
  if (!path) throw new Error(`неизвестное действие смены статуса: ${action}`)
  return path
}
